import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { exportLogs, getLogs, LogEntry, LogFilters } from "../../service/LogService";
import LogDetailDialog from "./LogDetailDialog";
import { formatTime, LevelChip, orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper, toIso } from "./logStyles";

const LEVELS = ["error", "warn", "info"];
const CATEGORIES = ["http", "security", "business", "communication", "integration", "cron", "client", "alert", "app"];
const REFRESH_INTERVAL_MS = 10000;

const emptyForm = { from: "", to: "", level: "", category: "", event: "", tenant: "", user: "", requestId: "", q: "" };

export default function LogTable() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  // Kommer man fra Overblik (fx "Fejl i loggen"), er filteret sat på forhånd: /logs?level=error
  const [searchParams] = useSearchParams();
  const fromUrl = Object.fromEntries(
    (Object.keys(emptyForm) as (keyof typeof emptyForm)[]).filter((key) => searchParams.get(key)).map((key) => [key, String(searchParams.get(key))])
  );
  const [form, setForm] = useState({ ...emptyForm, ...fromUrl });
  const [filters, setFilters] = useState<LogFilters>(fromUrl as LogFilters);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<LogEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selected, setSelected] = useState<LogEntry | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const data = await getLogs(filters, page, isMobile, navigate);
    if (data) {
      setRows(data.results);
      setHasMore(data.hasMore);
    }
    setLoading(false);
  }, [filters, page, isMobile, navigate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(fetchLogs, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  const setField = (field: keyof typeof emptyForm) => (event: any) => setForm({ ...form, [field]: event.target.value });

  const applyFilters = (values = form) => {
    setFilters({ ...values, from: toIso(values.from), to: toIso(values.to) });
    setPage(1);
  };

  const clearFilters = () => {
    setForm(emptyForm);
    applyFilters(emptyForm);
  };

  const followRequest = (requestId: string) => {
    const values = { ...emptyForm, requestId };
    setForm(values);
    applyFilters(values);
    setSelected(null);
  };

  const actorLabel = (row: LogEntry) => (row.actor?.role ? `${row.actor.role}${row.actor.userId ? ` ${row.actor.userId.slice(-6)}` : ""}` : "");

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%", my: 1 }}>
        <TextField label={t("logs.from")} type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.from} onChange={setField("from")} />
        <TextField label={t("logs.to")} type="datetime-local" size="small" InputLabelProps={{ shrink: true }} value={form.to} onChange={setField("to")} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>{t("logs.level")}</InputLabel>
          <Select label={t("logs.level")} value={form.level} onChange={setField("level")}>
            <MenuItem value="">{t("logs.all")}</MenuItem>
            {LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("logs.category")}</InputLabel>
          <Select label={t("logs.category")} value={form.category} onChange={setField("category")}>
            <MenuItem value="">{t("logs.all")}</MenuItem>
            {CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label={t("logs.event")} size="small" value={form.event} onChange={setField("event")} />
        <TextField label={t("logs.tenant")} size="small" value={form.tenant} onChange={setField("tenant")} />
        <TextField label={t("logs.user")} size="small" value={form.user} onChange={setField("user")} />
        <TextField label={t("logs.requestId")} size="small" value={form.requestId} onChange={setField("requestId")} />
        <TextField
          label={t("logs.search")}
          size="small"
          value={form.q}
          onChange={setField("q")}
          onKeyDown={(event) => event.key === "Enter" && applyFilters()}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <Button variant="contained" onClick={() => applyFilters()} sx={orangeButton}>
          {t("logs.filter")}
        </Button>
        <Button variant="outlined" onClick={clearFilters} sx={outlinedButton}>
          {t("logs.clear")}
        </Button>
        <Button variant="outlined" onClick={() => exportLogs(filters, isMobile, navigate)} sx={outlinedButton}>
          {t("logs.export")}
        </Button>
        <FormControlLabel
          control={<Switch checked={autoRefresh} onChange={() => setAutoRefresh(!autoRefresh)} color="warning" />}
          label={t("logs.autoRefresh")}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", marginTop: 1 }}>
        {t("logs.defaultRange")}
      </Typography>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("logs.time")}</StyledTableCell>
              <StyledTableCell>{t("logs.level")}</StyledTableCell>
              <StyledTableCell>{t("logs.category")}</StyledTableCell>
              <StyledTableCell>{t("logs.event")}</StyledTableCell>
              <StyledTableCell>{t("logs.message")}</StyledTableCell>
              <StyledTableCell>{t("logs.actor")}</StyledTableCell>
              <StyledTableCell>{t("logs.duration")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.id} hover sx={{ cursor: "pointer" }} onClick={() => setSelected(row)}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatTime(row.createdAt)}</TableCell>
                <TableCell>
                  <LevelChip level={row.level} />
                </TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.event}</TableCell>
                <TableCell sx={{ maxWidth: 480, wordBreak: "break-word" }}>{row.message}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{actorLabel(row)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{row.durationMs !== undefined ? `${row.durationMs} ms` : ""}</TableCell>
              </StyledTableRow>
            ))}
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                  {loading ? t("logs.loading") : t("logs.noEntries")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
        <Button variant="outlined" disabled={page === 1} onClick={() => setPage(page - 1)} sx={outlinedButton}>
          {t("logs.previous")}
        </Button>
        <Typography>{t("logs.page", { page })}</Typography>
        <Button variant="outlined" disabled={!hasMore} onClick={() => setPage(page + 1)} sx={outlinedButton}>
          {t("logs.next")}
        </Button>
      </Box>

      <LogDetailDialog entry={selected} onClose={() => setSelected(null)} onFollowRequest={followRequest} />
    </>
  );
}
